<template>
  <section class="admin-content" id="fill-assessment-page">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0; gap: 12px;">
            <router-link to="/assessments" class="btn btn-sm btn-outline-light" aria-label="Back to assessments"><i class="fe fe-arrow-left"></i></router-link>
            <div class="header-icon-wrap"><i class="fe fe-edit-3" style="font-size: 22px;"></i></div>
            <div>
              <h4 class="mb-0 font-weight-bold">{{ form ? form.title : "Assessment" }}</h4>
              <small style="opacity: .8;">{{ form && form.description ? form.description : "Answer what applies to your facility, then submit" }}</small>
            </div>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <div v-if="loading" class="card shadow-sm p-4 text-muted">Loading the form…</div>
          <div v-else-if="loadError" class="alert alert-danger">{{ loadError }}</div>

          <form v-else-if="form" @submit.prevent="submit">
            <div class="card shadow-sm mb-3 meta-card">
              <div class="card-body form-row align-items-end">
                <div class="form-group col-md-3 mb-md-0">
                  <label for="as-date" class="small font-weight-bold">Assessment date</label>
                  <input id="as-date" v-model="assessmentDate" type="date" class="form-control form-control-sm" :max="today" required />
                </div>
                <div class="form-group col-md-6 mb-md-0">
                  <label for="as-desc" class="small font-weight-bold">Notes <span class="text-muted">(optional)</span></label>
                  <input id="as-desc" v-model="description" class="form-control form-control-sm" maxlength="5000" />
                </div>
                <div class="col-md-3 text-md-right small text-muted">
                  {{ answeredCount }} of {{ itemCount }} answered
                  <div class="progress mt-1" style="height: 6px;" role="progressbar" :aria-valuenow="answeredCount" aria-valuemin="0" :aria-valuemax="itemCount">
                    <div class="progress-bar bg-success" :style="{ width: (itemCount ? (answeredCount / itemCount) * 100 : 0) + '%' }"></div>
                  </div>
                </div>
              </div>
            </div>

            <div v-for="cat in form.categories" :key="cat.id" class="card shadow-sm mb-3 cat-card">
              <div class="cat-header">
                <h6 class="mb-0 font-weight-bold">{{ cat.name }}</h6>
                <small v-if="cat.description" class="text-muted">{{ cat.description }}</small>
              </div>
              <div class="card-body pt-2">
                <div v-for="(item, idx) in cat.items" :key="item.id" class="q-row" :class="{ 'q-row--done': answered(item) }">
                  <div class="d-flex flex-wrap align-items-baseline" style="gap: 8px;">
                    <span class="q-num">{{ idx + 1 }}</span>
                    <div class="flex-fill">
                      <div class="font-weight-bold q-text" :id="'q-' + item.id">{{ item.name }}</div>
                      <div v-if="item.description" class="small text-muted">{{ item.description }}</div>
                      <div v-if="item.minimum_qty" class="small q-hint">Minimum required: {{ item.minimum_qty }}</div>
                    </div>
                  </div>

                  <div class="q-input">
                    <!-- Options -->
                    <div v-if="item.options.length" role="group" :aria-labelledby="'q-' + item.id" class="d-flex flex-wrap" style="gap: 6px;">
                      <template v-if="item.allow_multiple_response">
                        <label v-for="o in item.options" :key="o.id" class="opt" :class="{ 'opt--on': answers[item.id].option_ids.includes(o.id) }">
                          <input type="checkbox" class="sr-only" :value="o.id" v-model="answers[item.id].option_ids" />{{ o.name }}
                        </label>
                      </template>
                      <template v-else>
                        <label v-for="o in item.options" :key="o.id" class="opt" :class="{ 'opt--on': answers[item.id].option_id === o.id }">
                          <input type="radio" class="sr-only" :name="'opt-' + item.id" :value="o.id" v-model="answers[item.id].option_id" />{{ o.name }}
                        </label>
                        <button v-if="answers[item.id].option_id" type="button" class="btn btn-sm btn-link text-muted p-0 ml-1" @click="answers[item.id].option_id = null">Clear</button>
                      </template>
                    </div>

                    <!-- Quantity -->
                    <div v-if="item.response_data_format === 'number'" class="mt-2" style="max-width: 200px;">
                      <label :for="'qty-' + item.id" class="small text-muted mb-0">How many do you have?</label>
                      <input :id="'qty-' + item.id" v-model="answers[item.id].quantity" type="number" min="0" step="1" class="form-control form-control-sm" />
                      <div v-if="shortBy(item) > 0" class="small text-danger mt-1">Short by {{ shortBy(item) }}</div>
                    </div>
                    <template v-else-if="!item.options.length">
                      <div v-if="item.response_data_format === 'boolean'" class="d-flex" style="gap: 6px;" role="group" :aria-labelledby="'q-' + item.id">
                        <label v-for="v in ['Yes', 'No']" :key="v" class="opt" :class="{ 'opt--on': answers[item.id].response === v }">
                          <input type="radio" class="sr-only" :name="'bool-' + item.id" :value="v" v-model="answers[item.id].response" />{{ v }}
                        </label>
                      </div>
                      <input v-else-if="item.response_data_format === 'date'" v-model="answers[item.id].response" type="date"
                        class="form-control form-control-sm" style="max-width: 200px;" :aria-labelledby="'q-' + item.id" />
                      <input v-else-if="item.response_data_format === 'datetime'" v-model="answers[item.id].response" type="datetime-local"
                        class="form-control form-control-sm" style="max-width: 240px;" :aria-labelledby="'q-' + item.id" />
                      <input v-else v-model="answers[item.id].response" maxlength="255" class="form-control form-control-sm" :aria-labelledby="'q-' + item.id" />
                    </template>

                    <!-- Comment & evidence -->
                    <div class="mt-2 d-flex flex-wrap align-items-center" style="gap: 10px;">
                      <button v-if="!answers[item.id].showComment" type="button" class="btn btn-sm btn-link p-0" @click="answers[item.id].showComment = true">
                        <i class="fe fe-message-square mr-1"></i>Comment
                      </button>
                      <label class="btn btn-sm btn-link p-0 mb-0" :for="'file-' + item.id">
                        <i class="fe fe-paperclip mr-1"></i>Photo / document
                      </label>
                      <input :id="'file-' + item.id" type="file" class="sr-only" accept="image/png,image/jpeg,application/pdf" multiple
                        @change="addFiles(item, $event)" />
                      <span v-for="(f, i) in answers[item.id].files" :key="i" class="file-chip">
                        <i class="fe" :class="f.type === 'application/pdf' ? 'fe-file-text' : 'fe-image'"></i> {{ f.name }}
                        <button type="button" class="file-x" :aria-label="'Remove ' + f.name" @click="answers[item.id].files.splice(i, 1)"><i class="fe fe-x"></i></button>
                      </span>
                    </div>
                    <textarea v-if="answers[item.id].showComment" v-model="answers[item.id].comment" rows="2" maxlength="2000"
                      class="form-control form-control-sm mt-2" :aria-label="'Comment on ' + item.name"></textarea>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="error" class="alert alert-danger">{{ error }}</div>
            <div class="d-flex justify-content-end flex-wrap" style="gap: 10px;">
              <span class="small text-muted align-self-center" v-if="answeredCount < itemCount">
                {{ itemCount - answeredCount }} question{{ itemCount - answeredCount === 1 ? "" : "s" }} unanswered
              </span>
              <router-link to="/assessments" class="btn btn-light">Cancel</router-link>
              <button type="submit" class="btn btn-submit" :disabled="saving || !answeredCount">
                <span v-if="saving" class="spinner-border spinner-border-sm mr-1" role="status"></span>Submit assessment
              </button>
            </div>
          </form>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import { API, errorText, readFile } from "./assessments";

const MAX_FILES = 5;

export default {
  components: { Navbar },
  data() {
    const today = new Date().toISOString().slice(0, 10);
    return {
      form: null,
      answers: {},
      loading: true,
      loadError: "",
      saving: false,
      error: "",
      today,
      assessmentDate: today,
      description: "",
    };
  },
  computed: {
    items() {
      return this.form ? this.form.categories.reduce((all, c) => all.concat(c.items), []) : [];
    },
    itemCount() {
      return this.items.length;
    },
    answeredCount() {
      return this.items.filter((i) => this.answered(i)).length;
    },
  },
  created() {
    this.axios
      .get(`${API}/forms/${this.$route.params.id}`)
      .then((res) => {
        const form = res.data.data;
        const answers = {};
        form.categories.forEach((c) => c.items.forEach((i) => {
          answers[i.id] = { option_id: null, option_ids: [], quantity: "", response: "", comment: "", showComment: false, files: [] };
        }));
        this.answers = answers;
        this.form = form;
      })
      .catch((err) => (this.loadError = errorText(err, "Could not load the form.")))
      .finally(() => (this.loading = false));
  },
  methods: {
    answered(item) {
      const a = this.answers[item.id];
      if (!a) return false;
      return !!a.option_id || a.option_ids.length > 0 || a.quantity !== "" || String(a.response || "").trim() !== "";
    },
    shortBy(item) {
      const q = this.answers[item.id].quantity;
      return item.minimum_qty && q !== "" ? Math.max(item.minimum_qty - Number(q), 0) : 0;
    },
    addFiles(item, event) {
      const files = Array.from(event.target.files || []);
      event.target.value = "";
      const list = this.answers[item.id].files;
      if (list.length + files.length > MAX_FILES) {
        this.$toasted.error(`Up to ${MAX_FILES} files per question.`, { position: "top-center", duration: 4000 });
        return;
      }
      files.forEach((f) =>
        readFile(f)
          .then((data) => list.push(data))
          .catch((e) => this.$toasted.error(e.message, { position: "top-center", duration: 4000 }))
      );
    },
    payload() {
      const responses = this.items
        .filter((i) => this.answered(i) || this.answers[i.id].comment || this.answers[i.id].files.length)
        .map((i) => {
          const a = this.answers[i.id];
          const r = { form_item_id: i.id };
          if (i.allow_multiple_response && a.option_ids.length) r.option_ids = a.option_ids;
          else if (a.option_id) r.option_id = a.option_id;
          if (a.quantity !== "") r.quantity = Number(a.quantity);
          if (String(a.response || "").trim()) r.response = String(a.response).trim();
          if (a.comment.trim()) r.comment = a.comment.trim();
          if (a.files.length) r.files = a.files.map((f) => f.data);
          return r;
        });
      return {
        inspect_form_id: this.form.id,
        assessment_date: this.assessmentDate,
        description: this.description || null,
        responses,
      };
    },
    submit() {
      if (this.answeredCount < this.itemCount
        && !window.confirm(`${this.itemCount - this.answeredCount} question(s) are unanswered and will count as gaps where they are scored. Submit anyway?`)) {
        return;
      }
      this.saving = true;
      this.error = "";
      this.axios
        .post(`${API}/submissions`, this.payload())
        .then((res) => {
          this.$toasted.success("Assessment submitted", { position: "top-center", duration: 4000 });
          this.$router.push(`/assessments/submissions/${res.data.data.id}`);
        })
        .catch((err) => {
          this.error = errorText(err, "The assessment could not be submitted.");
          window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
        })
        .finally(() => (this.saving = false));
    },
  },
};
</script>

<style scoped>
.page-header { background: linear-gradient(135deg, #115e59 0%, #0d9488 100%); }
.header-icon-wrap { width: 48px; height: 48px; background: rgba(255,255,255,0.15); border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.meta-card, .cat-card { border: none; border-radius: 12px; }
.cat-header { padding: 14px 20px 6px; border-bottom: 1px solid #f1f5f9; }
.q-row { padding: 14px 0; border-bottom: 1px solid #f1f5f9; }
.q-row:last-child { border-bottom: none; }
.q-num { display: inline-flex; width: 26px; height: 26px; border-radius: 50%; background: #f1f5f9; color: #475569; font-size: .75rem; font-weight: 700; align-items: center; justify-content: center; flex-shrink: 0; }
.q-row--done .q-num { background: #ccfbf1; color: #115e59; }
.q-text { font-size: .92rem; }
.q-hint { color: #0f766e; }
.q-input { padding-left: 34px; margin-top: 8px; }
.opt { border: 1px solid #cbd5e1; border-radius: 20px; padding: 4px 14px; font-size: .85rem; cursor: pointer; margin: 0; background: #fff; }
.opt:focus-within { box-shadow: 0 0 0 3px rgba(13,148,136,.3); }
.opt--on { background: #0d9488; border-color: #0d9488; color: #fff; }
.file-chip { display: inline-flex; align-items: center; gap: 4px; background: #f1f5f9; border-radius: 12px; padding: 1px 8px; font-size: .75rem; }
.file-x { border: none; background: none; padding: 0 0 0 2px; color: #64748b; }
.btn-submit { background: linear-gradient(135deg, #115e59, #0d9488); border: none; color: #fff; border-radius: 8px; font-weight: 600; }
.btn-submit:hover { opacity: .9; color: #fff; }
@media (max-width: 576px) { .q-input { padding-left: 0; } }
</style>
